import EvidencePDFViewer from 'components/Grading/EvidencePDFViewer';
import EvidenceRichTextSubmissionViewer from 'components/Grading/EvidenceRichTextSubmissionViewer';
import FileSelector from 'components/Grading/FileSelector';
import FileViewer from 'components/Grading/FileViewer';
import LegacyFeedbackRenderer from 'components/Grading/LegacyFeedbackRenderer';
import RubricRenderer from 'components/Grading/RubricRenderer';
import FeedbackRenderer from 'components/InstructorViewSubmission/FeedbackRenderer';
import Header from 'components/InstructorViewSubmission/Header';
import { isEqual } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Box,
    Button,
    ButtonGroup,
    Container,
    Dropdown,
    Icon,
    IconButton,
    SheetViewer,
    Stack,
    styled,
    Text,
    VideoViewer,
} from 'shell/components';
import { getS3FileURL } from 'utils/helper';

const Tablet = ({
    currentAssignment,
    currentSubmission,
    selectedCriterionTitle,
    highlights,
    selectedFileId,
    setSelectedFileId,
    tab,
    setTab,
    selectedCriterionId,
    setSelectedCriterionId,
    selectedFeedbackId,
    setSelectedFeedbackId,
    selectedInlineCommentId,
    setSelectedInlineCommentId,
    isEditing,
    form,
    setForm,
    isCurrentFormValid,
    gradeOverrides,
    setGradeOverrides,
    isDocumentFullScreen,
    setIsDocumentFullScreen,
    onClickEdit,
    onClickEditCancel,
    onClickEditSave,
    onSelect,
    updateSubmission,
    handleCriterionNavigation,
}) => {
    const { t } = useTranslation('assignment');

    const currentFile = currentSubmission.files.find(({ id }) => id === selectedFileId);
    const { bucket, key, name } = currentFile ?? {};
    const url = useMemo(() => getS3FileURL({ bucket, key, name }), [bucket, key, name]);

    const renderableHighlights = highlights
        .filter(({ value }) => {
            if (typeof value === 'string') return true;
            return String(value.file_id) === String(selectedFileId);
        })
        .map(({ id, value }) => {
            if (typeof value === 'string') return { id, value };
            return { id: id, index: value.index, value: value.highlight ?? value.quote };
        });

    return (
        <CustomContainer>
            <Header />
            <ContentStack>
                <TopStack isDocumentFullScreen={isDocumentFullScreen}>
                    {isDocumentFullScreen && (
                        <BannerBox>
                            <Text color={'inherit'}>
                                {tab === 'grades' && selectedCriterionId
                                    ? t(
                                          'grading-section.document-preview.criterion-annotations',
                                          `Currently viewing annotations for the criterion "{{criterionTitle}}."`,
                                          {
                                              criterionTitle: selectedCriterionTitle,
                                          }
                                      )
                                    : tab === 'feedback' && selectedFeedbackId
                                      ? t(
                                            'grading-section.document-preview.feedback-annotations',
                                            `Currently viewing annotations for feedback.`
                                        )
                                      : t(
                                            'grading-section.document-preview.inline-comments-annotations',
                                            `Currently viewing annotations for inline comments.`
                                        )}{' '}
                            </Text>
                        </BannerBox>
                    )}
                    {currentFile && (
                        <Box sx={{ height: 'calc(100% - 44px)' }}>
                            {currentFile.type === 'application/pdf' ? (
                                <EvidencePDFViewer
                                    url={url}
                                    highlights={renderableHighlights}
                                    inlineComments={form?.inline_comments}
                                    setInlineComments={(func) => {
                                        if (isEqual(form, func(form))) return;
                                        setForm(func);
                                        updateSubmission(func(form));
                                    }}
                                    selectedInlineCommentId={selectedInlineCommentId}
                                    setSelectedInlineCommentId={setSelectedInlineCommentId}
                                    isFullScreen={isDocumentFullScreen}
                                    setIsFullScreen={setIsDocumentFullScreen}
                                />
                            ) : currentFile.type === 'text/csv' ? (
                                <SheetViewer highlights={renderableHighlights} url={url} />
                            ) : currentFile.type === 'text/html' && currentFile.lms_text_entry ? (
                                <EvidenceRichTextSubmissionViewer
                                    content={currentFile.content}
                                    highlights={renderableHighlights}
                                    url={url}
                                />
                            ) : currentFile.type.startsWith('video/') ||
                              currentFile.type.startsWith('application/mp4') ? (
                                <VideoViewer
                                    highlights={renderableHighlights}
                                    mimeType={currentFile.type}
                                    transcript={currentFile.transcript}
                                    url={url}
                                />
                            ) : (
                                <FileViewer docUrl={url} fileType={currentFile.type} />
                            )}
                        </Box>
                    )}
                    <FileSelector
                        submission={currentSubmission}
                        selectedFileId={selectedFileId}
                        onClick={(file) => setSelectedFileId(file.id)}
                    />
                </TopStack>
                <BottomStack isDocumentFullScreen={isDocumentFullScreen}>
                    <ControlsStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <ButtonGroup
                            options={[
                                { key: 'grades', value: t('common:ui.grades', 'Grades') },
                                { key: 'feedback', value: t('common:ui.feedback', 'Feedback'), disabled: isEditing },
                            ]}
                            selected={tab}
                            onChange={setTab}
                        />
                        {tab === 'grades' && (
                            <>
                                {isEditing ? (
                                    <Stack direction={'row'} spacing={2}>
                                        <Button color={'neutral'} variant={'tertiary'} onClick={onClickEditCancel}>
                                            {t('common:controls.cancel', 'Cancel')}
                                        </Button>
                                        <Button disabled={!isCurrentFormValid} onClick={onClickEditSave}>
                                            {t('common:controls.save', 'Save')}
                                        </Button>
                                    </Stack>
                                ) : (
                                    <Button
                                        color={'neutral'}
                                        startIcon={<Icon kind={'edit'} />}
                                        variant={'tertiary'}
                                        onClick={onClickEdit}
                                    >
                                        {t('grading-section.edit-grades', 'Edit Grades')}
                                    </Button>
                                )}
                            </>
                        )}
                        {tab === 'feedback' && currentSubmission.ai?.feedback?.feedback_type !== 'templated' && (
                            <Dropdown
                                id={'view-submission-more-options-dropdown'}
                                options={[
                                    {
                                        key: 'copy-all',
                                        value: t('grading-section.actions.copy-all-feedback', 'Copy all feedback'),
                                        icon: 'copy',
                                    },
                                    {
                                        key: 'delete',
                                        value: t('grading-section.actions.delete-all-feedback', 'Delete all feedback'),
                                        icon: 'delete',
                                    },
                                ]}
                                rootComponent={
                                    <IconButton
                                        aria-label={'more options'}
                                        kind={'moreHorizontal'}
                                        size={'medium'}
                                        variant={'square'}
                                    />
                                }
                                onSelect={onSelect}
                            />
                        )}
                    </ControlsStack>
                    {tab === 'grades' && (
                        <RubricRenderer
                            disabled={!isEditing}
                            showGraded={false}
                            showGradingExplanationInput={false}
                            assignment={currentAssignment}
                            currentSubmission={currentSubmission}
                            selectedFileId={selectedFileId}
                            setSelectedFileId={setSelectedFileId}
                            locations={highlights}
                            form={form}
                            setForm={setForm}
                            gradeOverrides={gradeOverrides}
                            setGradeOverrides={setGradeOverrides}
                            selectedCriterionId={selectedCriterionId}
                            setSelectedCriterionId={setSelectedCriterionId}
                            setShowFailedDialog={() => {}}
                            handleCriterionNavigation={handleCriterionNavigation}
                            hideCriterionNavigation={isEditing}
                        />
                    )}
                    {tab === 'feedback' && (
                        <Stack sx={{ flex: 1, overflow: 'auto' }}>
                            {currentSubmission.is_failed || typeof currentSubmission.grading.feedback === 'string' ? (
                                <LegacyFeedbackRenderer
                                    currentSubmission={currentSubmission}
                                    form={form}
                                    setForm={setForm}
                                    setShowFailedDialog={() => {}}
                                />
                            ) : (
                                <FeedbackRenderer
                                    assignment={currentAssignment}
                                    currentSubmission={currentSubmission}
                                    selectedFileId={selectedFileId}
                                    setSelectedFileId={setSelectedFileId}
                                    locations={highlights}
                                    form={form}
                                    setForm={(func) => {
                                        setForm(func);
                                        updateSubmission(func(form));
                                    }}
                                    selectedFeedbackId={selectedFeedbackId}
                                    setSelectedFeedbackId={setSelectedFeedbackId}
                                    handleCriterionNavigation={handleCriterionNavigation}
                                />
                            )}
                        </Stack>
                    )}
                </BottomStack>
            </ContentStack>
        </CustomContainer>
    );
};

const CustomContainer = styled(Container)(({ theme }) => {
    return {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        minHeight: '100%',
        padding: 0,
        paddingTop: '81px',
        background: theme.palette.ensightful.neutral[100],
    };
});

const ContentStack = styled(Stack)(() => {
    return {
        height: 'calc(100vh - 81px)',
        overflow: 'auto',
    };
});

const TopStack = styled(Stack)(({ isDocumentFullScreen }) => {
    return {
        position: 'relative',
        height: isDocumentFullScreen ? '100%' : '48%',
        transition: 'height 150ms',
    };
});

const BannerBox = styled(Box)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(1, 5),
        background: theme.palette.ensightful.warning[200],
        color: theme.palette.ensightful.warning[900],
    };
});

const BottomStack = styled(Stack)(({ theme, isDocumentFullScreen }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        height: isDocumentFullScreen ? 0 : '52%',
        background: theme.palette.ensightful.shades[0],
        transition: 'height 150ms',
    };
});

const ControlsStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(3, 5),
    };
});

export default Tablet;
